// localeStore is root because it is required in config files
import { defineStore } from 'pinia';
import i18next from 'i18next';
import { useCookies } from '@vueuse/integrations/useCookies';
import { ref } from 'vue';
import { defaultLocale } from '@/config/constants';
import { computed } from 'vue';
import { useLocalesQuery } from '@/api/queries/locales/useLocalesQuery';
import { i18nNamespaces } from '@/config/i18n';
import { useToast } from 'vue-toastification';

export type Locale = {
  id: number;
  iso_code: string;
  iso_code_639: string;
  label: string;
  flag: string;
}

export const useLocaleStore = defineStore('locales', () => {
  const { getLocaleById } = useLocalesQuery();
  const toast = useToast();

  const loading = ref(false);

  // This variable is used to allow Promise from a separate function
  let loadingSuspense: Promise<void> | undefined;

  const currentLocale = ref<Locale>(
    useCookies().get('locale') ?? defaultLocale,
  );

  const currentLang = computed(() => {
    return currentLocale.value.iso_code_639.slice(0, 2);
  });

  const isRtl = computed(() => {
    return currentLocale.value.iso_code_639.startsWith('ar');
  });

  async function updateLocale(localeId?: number) {
    loading.value = true;

    if (!localeId) return;

    const locale = getLocaleById(localeId);

    // Waits for loadingSuspense Promise to be fulfilled to avoid language change while
    // namespaces are being loaded.
    // This would cause namespaces of the new lang not to be updated.
    if (loadingSuspense) await loadingSuspense;

    await i18next.changeLanguage(locale.iso_code_639, () => {
      currentLocale.value = locale;
      useCookies().set('locale', locale);
      loading.value = false;

      document.documentElement.setAttribute('lang', locale.iso_code_639);
      document.documentElement.dir = isRtl.value ? 'rtl' : 'ltr';
      document.documentElement.style.setProperty('--v-locale--is-ltr', isRtl.value ? 'rtl' : 'ltr');
      toast.updateDefaults({ rtl: isRtl.value });
    });
  }

  async function loadNamespaces(namespaces: keyof typeof i18nNamespaces) {
    const missingNamespaces = i18nNamespaces[namespaces].filter(
      namespace => !i18next.hasLoadedNamespace(namespace),
    );

    // Assigns loadNamespaces Promise with specific params so that loadingSuspense can be awaited in other function.
    loadingSuspense = i18next.loadNamespaces(missingNamespaces);
    await loadingSuspense;
  }

  return { loading, currentLocale, currentLang, updateLocale, loadNamespaces, isRtl };
});
